import { POWERUP_KINDS, type PowerupKind } from '@/physics/powerups';

/**
 * ItemButton.ts — THE use-item button (M12): one big round touch button in the
 * lower-right thumb zone that doubles as the item slot display, replacing the
 * old three-button cooldown loadout. It shows: an empty dashed slot, the
 * Mario-Kart roulette flicker while a collected item rolls, the held item's
 * icon (pulsing = ready to fire), and a draining ring while an effect runs.
 * Tapping it is a one-shot FIRE that the InputManager merges with the
 * keyboard/gamepad edges. `deny()` shakes it when a fire is refused (magnet/
 * kick too far from the ball).
 *
 * M145 (owner: "more apparent and big"): the button grew (112px, sized in
 * ui.css), wears the item's NAME above it, and pops a ring when an item
 * lands and again when it fires.
 *
 * Purely the player's HUD; swappable DOM/CSS so real art can drop in later.
 * The 3D game never reads it directly — Game pushes the player's slot state
 * each frame via `setState`.
 */

export type ItemButtonPhase = 'empty' | 'roulette' | 'held' | 'active';

export interface ItemButtonState {
  phase: ItemButtonPhase;
  /** The held/active item (null while empty or mid-roulette). */
  kind: PowerupKind | null;
  /** 0..1 effect time remaining — drains the ring while active. */
  fraction: number;
}

/** Display names — shared with the HOW TO PLAY item gallery (M86). */
export const ITEM_NAMES: Record<PowerupKind, string> = {
  boost: 'Boost',
  magnet: 'Magnet',
  dozer: 'Bulldozer',
  shield: 'Shield',
  kick: 'Kick',
  tackle: 'Slide Tackle',
  wall: 'Free Kick Wall',
  offside: 'Offside Trap',
  pibe: 'Pibe de Oro',
  studs: 'Studs Out',
  scorpion: 'Scorpion Kick',
  mud: 'Slippery Mud',
};

/** Crisp inline SVG icons (fill: currentColor) — restyleable from CSS.
 *  Shared with the HOW TO PLAY item gallery (M86) so the guide shows the
 *  exact icons the in-race button will. */
export const ITEM_ICONS: Record<PowerupKind, string> = {
  boost: '<svg viewBox="0 0 24 24"><path d="M13.4 2 5 13.6h5L8.6 22 19 9.9h-5.5L13.4 2z"/></svg>',
  magnet:
    '<svg viewBox="0 0 24 24"><path d="M5 3h5v9a2 2 0 0 0 4 0V3h5v9A7 7 0 0 1 5 12V3z"/>' +
    '<path fill-opacity="0.45" d="M5 3h5v4H5zM14 3h5v4h-5z"/></svg>',
  dozer:
    '<svg viewBox="0 0 24 24"><path d="M4 6v11h14.5L15 6H4z"/>' +
    '<path d="M3 19h18v2.2H3z"/><path fill-opacity="0.45" d="M19.6 9.5 22 11v6h-2.4z"/></svg>',
  shield:
    '<svg viewBox="0 0 24 24"><path d="M12 2l8 3.4V12c0 4.8-3.4 8.7-8 10-4.6-1.3-8-5.2-8-10V5.4L12 2z"/></svg>',
  kick:
    '<svg viewBox="0 0 24 24"><circle cx="15.5" cy="12" r="6"/>' +
    '<path fill-opacity="0.6" d="M1.5 8h7v2h-7zM3 11h5.5v2H3zM1.5 14h7v2h-7z"/></svg>',
  // A car thrown sideways into the slide, skid lines trailing.
  tackle:
    '<svg viewBox="0 0 24 24"><g transform="rotate(38 14 13)">' +
    '<rect x="9" y="9" width="11" height="6.5" rx="1.6"/>' +
    '<rect x="11" y="7.6" width="6" height="2.4" rx="1"/></g>' +
    '<path fill-opacity="0.6" d="M2 16h6v2H2zM3.5 19h6v2h-6zM1 13h5v2H1z"/></svg>',
  // Three defenders shoulder to shoulder — the free-kick wall.
  wall:
    '<svg viewBox="0 0 24 24"><circle cx="6" cy="6" r="2.2"/><rect x="3.8" y="9" width="4.4" height="12" rx="1.4"/>' +
    '<circle cx="12" cy="5" r="2.2"/><rect x="9.8" y="8" width="4.4" height="13" rx="1.4"/>' +
    '<circle cx="18" cy="6" r="2.2"/><rect x="15.8" y="9" width="4.4" height="12" rx="1.4"/></svg>',
  // The linesman's raised flag.
  offside:
    '<svg viewBox="0 0 24 24"><rect x="6" y="2" width="2.2" height="20" rx="1"/>' +
    '<path d="M8.2 3h12l-3.2 4 3.2 4h-12z"/></svg>',
  // The golden star — number 10's halo.
  pibe:
    '<svg viewBox="0 0 24 24"><path d="M12 1.6 15 8.4l7.4.7-5.6 4.9 1.7 7.2L12 17.4l-6.5 3.8 1.7-7.2L1.6 9.1 9 8.4z"/>' +
    '<circle fill-opacity="0.5" cx="12" cy="12" r="3"/></svg>',
  // A spiked wheel — the studs burst.
  studs:
    '<svg viewBox="0 0 24 24"><path d="M12 1l1.8 3.4 3.4-1.8-1.1 3.7 3.8.6-2.9 2.5 2.9 2.5-3.8.6 1.1 3.7-3.4-1.8L12 23l-1.8-3.4-3.4 1.8 1.1-3.7-3.8-.6 2.9-2.5-2.9-2.5 3.8-.6-1.1-3.7 3.4 1.8z"/>' +
    '<circle fill-opacity="0.45" cx="12" cy="12" r="3.4"/></svg>',
  // M145 (owner: "improve the icon for the scorpion kick"): an actual scorpion
  // — body, pincers, legs — whipping its tail up and over its own back to
  // strike a football with the stinger, which is exactly what the kick is.
  // (The old icon was a plain ball with an arrow.)
  scorpion:
    '<svg viewBox="0 0 24 24"><ellipse cx="8.4" cy="18.4" rx="4.4" ry="2.5"/>' +
    '<circle cx="13.4" cy="18.7" r="1.8"/>' +
    '<path d="M14.6 17.5c1-1.9 2.9-2.5 4.2-1.6-1.5.1-2.4.9-2.8 2.2z"/>' +
    '<path d="M14.7 20c1.2 1.6 3 1.9 4.1.9-1.4 0-2.3-.7-2.9-1.8z"/>' +
    '<path d="M5.2 20.2 3.6 22.4M8 20.9l-.5 2.2M10.9 20.5l1 2.1" stroke="currentColor" stroke-width="1.1" stroke-linecap="round" fill="none"/>' +
    '<circle cx="3.9" cy="15.3" r="1.95"/><circle cx="3" cy="11.6" r="1.8"/>' +
    '<circle cx="4.2" cy="8" r="1.65"/><circle cx="7" cy="5.5" r="1.5"/><circle cx="10.4" cy="4.6" r="1.4"/>' +
    '<path d="M11.2 3.3c2.3-.3 4 .8 4.9 3.1l-2.6-.6c-.7-.5-1.5-.6-2.4-.3z"/>' +
    '<path fill-rule="evenodd" d="M19.6 3.6a3.5 3.5 0 1 0 0 7a3.5 3.5 0 1 0 0-7zM19.6 5.6l1.43 1.04-.55 1.68h-1.76l-.55-1.68z"/>' +
    '<path d="M21.6 12.2l1 1.5M19.3 12.7v1.8M17 12.2l-1 1.5" stroke="currentColor" stroke-width="1" stroke-linecap="round" fill="none" opacity="0.6"/></svg>',
  // A puddle with splashes — the mud pour.
  mud:
    '<svg viewBox="0 0 24 24"><ellipse cx="12" cy="16.5" rx="9.5" ry="4"/>' +
    '<circle fill-opacity="0.6" cx="7" cy="8.5" r="1.7"/><circle fill-opacity="0.6" cx="13" cy="6.5" r="2.2"/>' +
    '<circle fill-opacity="0.6" cx="17.5" cy="9.5" r="1.4"/></svg>',
};

const EMPTY_ICON = '<span class="item-btn-q">?</span>';
const ROULETTE_STEP_MS = 70;

export class ItemButton {
  private root: HTMLDivElement;
  private btn: HTMLButtonElement;
  private ring: HTMLDivElement;
  private icon: HTMLDivElement;
  private nameEl: HTMLDivElement;
  private pressed = false;
  private shownPhase: ItemButtonPhase | null = null;
  private shownKind: PowerupKind | null = null;
  private rouletteIdx = 0;
  private rouletteAt = 0;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'item-button';

    this.btn = document.createElement('button');
    this.btn.className = 'item-btn';
    this.btn.setAttribute('aria-label', 'Use item');

    this.ring = document.createElement('div');
    this.ring.className = 'item-btn-ring';

    this.icon = document.createElement('div');
    this.icon.className = 'item-btn-icon';
    this.icon.innerHTML = EMPTY_ICON;

    const key = document.createElement('div');
    key.className = 'item-btn-key';
    key.textContent = 'SPACE';

    // M145: the travelling "ready" ring + the one-shot land/fire burst.
    const ping = document.createElement('div');
    ping.className = 'item-btn-ping';
    const burst = document.createElement('div');
    burst.className = 'item-btn-burst';

    // M145: the held item's name, above the button (a thumb covers below).
    this.nameEl = document.createElement('div');
    this.nameEl.className = 'item-btn-name';

    this.btn.append(ping, this.ring, this.icon, burst);
    this.root.append(this.nameEl, this.btn, key);
    container.appendChild(this.root);

    this.btn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation(); // don't let the joystick (window listener) also react
      this.pressed = true;
      this.btn.classList.add('pressing');
    });
    const release = (): void => this.btn.classList.remove('pressing');
    this.btn.addEventListener('pointerup', release);
    this.btn.addEventListener('pointercancel', release);
    this.btn.addEventListener('pointerleave', release);
  }

  /** One-shot press since the last call (each tap fires exactly once). */
  read(): boolean {
    const p = this.pressed;
    this.pressed = false;
    return p;
  }

  /** M112 (demo race): is a tap queued? NON-destructive — the tap stays
   *  latched for the race's own read() once the tutorial freeze lifts. */
  peekPressed(): boolean {
    return this.pressed;
  }

  /** M112 coach cue, M115 escalating: 0 = off, 1 = the standing FIRE cue,
   *  2 = unmissable (the player has been sitting on the lesson a while). */
  setHighlight(level: 0 | 1 | 2): void {
    this.btn.classList.toggle('coach', level > 0);
    this.btn.classList.toggle('coach-urgent', level === 2);
  }

  /** Update the slot display (called each frame by Game). */
  setState(s: ItemButtonState): void {
    // The drain ring: full except while an effect runs down.
    this.ring.style.setProperty('--charge', (s.phase === 'active' ? s.fraction : 1).toFixed(3));

    if (s.phase === 'roulette') {
      // Mario-Kart flicker: cycle the icons fast until the engine's roulette ends.
      const now = performance.now();
      if (now - this.rouletteAt > ROULETTE_STEP_MS) {
        this.rouletteAt = now;
        this.rouletteIdx = (this.rouletteIdx + 1) % POWERUP_KINDS.length;
        const kind = POWERUP_KINDS[this.rouletteIdx];
        this.icon.innerHTML = ITEM_ICONS[kind];
        this.root.dataset.kind = kind; // the colour flickers with the icon
      }
    } else if (s.phase !== this.shownPhase || s.kind !== this.shownKind) {
      if (s.kind) {
        this.icon.innerHTML = ITEM_ICONS[s.kind];
        this.root.dataset.kind = s.kind;
        this.nameEl.textContent = ITEM_NAMES[s.kind];
        this.btn.setAttribute('aria-label', `Use ${ITEM_NAMES[s.kind]}`);
      } else {
        this.icon.innerHTML = EMPTY_ICON;
        delete this.root.dataset.kind;
        this.btn.setAttribute('aria-label', 'Use item (empty)');
      }
    }
    if (s.phase !== this.shownPhase) {
      // The wrapper carries the phase too (the name label reads it).
      this.btn.dataset.phase = s.phase;
      this.root.dataset.phase = s.phase;
      // An item just LANDED (held) or just FIRED (active): pop the burst ring.
      // Not on the very first paint — a race that opens mid-effect stays quiet.
      if (this.shownPhase !== null && (s.phase === 'held' || s.phase === 'active')) this.pop();
    }
    this.shownPhase = s.phase;
    this.shownKind = s.kind;
  }

  /** One burst ring off the button (an item landed or fired). */
  private pop(): void {
    this.btn.classList.remove('pop');
    void this.btn.offsetWidth; // restart the animation
    this.btn.classList.add('pop');
  }

  /** Refused fire (too far from the ball) — shake the button. */
  deny(): void {
    this.btn.classList.remove('deny');
    void this.btn.offsetWidth; // restart the animation
    this.btn.classList.add('deny');
  }

  /** Hide the button while menus are up (it's racing HUD, not menu UI). */
  setVisible(visible: boolean): void {
    this.root.style.display = visible ? '' : 'none';
  }

  /** Left-handed layout (M31): move the button to the lower-LEFT corner (CSS). */
  setLefty(lefty: boolean): void {
    this.root.classList.toggle('lefty', lefty);
  }

  dispose(): void {
    this.root.remove();
  }
}
